import { call, put, takeLeading, takeLatest, debounce, select } from 'redux-saga/effects';

import {
  isOnline,
  closeSocketConnection,
  openSocketConnection,
  getSocket,
} from 'utils/socket';
import vaccineStatuses from 'enums/vaccineStatuses';
import history from 'store/history';
import api from 'services/api';
import authUtils from 'utils/auth';
import actions from './actions';
import errorActions from '../error/actions';
import dxApi from 'services/dxApi';

const {
  Types: {
    LOGIN_REQUEST,
    LOGIN_WITH_SERVICE,
    RESET_PASSWORD_REQUEST,
    REQUEST_RESET_REQUEST,
    LOGOUT_REQUEST,
    LOAD_PROFILE_REQUEST,
    LOAD_MEMBERSHIP_REQUEST,
    CHANGE_FAVORITE_LOCATION_REQUEST,
    CHANGE_PROFILE_INFO_REQUEST,
    CHECK_FAMILY_REQUEST,
    CHANGE_PASSWORD_REQUEST,
    UPDATE_PASSWORD_REQUEST,
    CHECK_MEMBER_STATUS_REQUEST,
    CREATE_MAGIC_LINK_REQUEST,
    SET_VACCINE_STATUS_REQUEST,
    VALIDATE_MAGIC_LINK_REQUEST,
    SET_PASSWORD_MAGIC_LINK_REQUEST,
  },
  Creators,
} = actions;

const {
  Creators: { setError },
} = errorActions;

function* loginWithService({ data }) {
  if (data?.error) {
    yield put(setError('Cannot find member, please check details and try again'));
  } else {
    authUtils.setToken(data.token, data.sfId);
    api.setAuthTokenToHeader(data.token);

    yield put(Creators.loginSuccess({ ...data }));
    const socket = getSocket();
    if (socket.disconnected) openSocketConnection();
    yield isOnline({ id: data.user.sfId });
  }
}

function* login({ payload }) {
  try {
    const { email, password, idCA } = payload;

    // if (navigator.cookieEnabled) yield call(authUtils.clearCache);

    const { data } = yield call(dxApi.post, '/account/auth-nv2', {
      email,
      password,
      // idCA,
    });
    if (!data.success) {
      yield put(Creators.loginFailure(data.message));
      // yield put(setError(data.message));
    } else {
      const loginData = data.data;
      authUtils.setToken(loginData.token, loginData.user.sfId);
      const isRememberMe = localStorage.getItem('rememberMe');
      if (isRememberMe === 'true') {
        authUtils.setRememberMeToken(loginData.token);
      }

      dxApi.setAuthTokenToHeader(loginData.token);
      localStorage.removeItem('polkMemberPopupClosed');
      localStorage.removeItem('isPolkMember');
      localStorage.removeItem('polkPopupShown');
      localStorage.removeItem('tncApiChecked');
      if (loginData.user.isPolkMember) {
        localStorage.setItem('isPolkMember', 'true');
      }

      yield put(Creators.loginSuccess(loginData.user));
      const socket = getSocket();
      if (socket.disconnected) openSocketConnection();
      yield isOnline({ id: loginData.user.sfId });

      // Check if there's a callback URL in sessionStorage
      const callback = sessionStorage.getItem('callback');

      // Store the original callback URL before potentially navigating to onboarding
      if (callback && callback !== '/onboarding' && callback !== '/') {
        // Save the original callback for after onboarding completion
        sessionStorage.setItem('originalCallback', callback);
      }

      // If user needs onboarding, they must complete it first
      if (loginData.user.isOnboardingDisplay) {
        yield call(history.push, '/onboarding');
      }
      // Otherwise, if there's a callback, redirect to it
      else if (callback && callback !== '/' && callback !== '/onboarding') {
        yield call(history.push, callback);
        // Clear callback after using it
        sessionStorage.removeItem('callback');
        sessionStorage.removeItem('originalCallback');
      }
    }
  } catch (err) {
    console.log('login err: ', err);
    yield put(Creators.loginFailure(err));
    yield put(setError('Login error.'));
  }
}

function* logout() {
  try {
    yield call(authUtils.clearCache);

    // Clear onboarding flags on logout
    localStorage.removeItem('onboardingCompleted');
    localStorage.removeItem('onboardingInProgress');
    localStorage.removeItem('polkMemberPopupClosed');
    localStorage.removeItem('isPolkMember');
    localStorage.removeItem('polkPopupShown');
    localStorage.removeItem('tncApiChecked');

    authUtils.removeToken();
    authUtils.removeRememberMeToken();

    // Special case for magic-link-password - don't set it as callback
    if (history.location.pathname === '/magic-link-password') {
      // Clear any existing callback to prevent redirection issues
      sessionStorage.removeItem('callback');
      sessionStorage.removeItem('originalCallback');
    }
    // Save the current path before logging out so we can redirect back after login
    else if (
      history.location.pathname !== '/login' &&
      history.location.pathname !== '/'
    ) {
      sessionStorage.setItem('callback', history.location.pathname);
    }

    yield put(Creators.logoutSuccess());
    yield closeSocketConnection();
  } catch (err) {
    yield put(Creators.logoutFailure(err));
  }
}

function* requestReset({ email }) {
  try {
    const { data } = yield call(dxApi.post, '/account/request-reset-password-nv2', {
      email,
    });
    if (!data.success) {
      yield put(Creators.requestResetFailure(data.message));
    } else {
      yield put(Creators.requestResetSuccess());
      yield call(history.push, '/request_success');
    }
  } catch (err) {
    const errorMessage =
      err.response.status === 404
        ? 'Cannot find this email'
        : 'Sorry, something went wrong, please try again in a minute or two';

    yield put(Creators.requestResetFailure(errorMessage));
  }
}

function* resetPassword({ payload }) {
  try {
    const { password } = payload;
    const receivedToken = history.location.search.replace('?token=', '');
    const resetToken = receivedToken.split('&')[0];

    const { data } = yield call(dxApi.post, `/account/reset-password-nv2`, {
      resetToken,
      password,
    });

    if (data.success) {
      yield put(Creators.resetPasswordSuccess());
      yield call(history.push, '/create_success');
    } else {
      yield put(Creators.resetPasswordFailure(data.message));
      yield put(setError(data.message));
    }
  } catch (err) {
    yield put(Creators.resetPasswordFailure(err));
    yield put(
      setError('Sorry, something went wrong, please try again in a minute or two')
    );
  }
}

function* updatePassword({ payload }) {
  try {
    const { data } = yield call(api.post, `/auth/update_password`, payload);
    if (data.success) {
      yield put(Creators.updatePasswordSuccess());
      yield put(setError('Your password was updated'));
    }
  } catch (err) {
    yield put(Creators.updatePasswordFailure(err));
    yield put(
      setError(
        'Sorry, could not update your password. Please try again in a minute or two.'
      )
    );
  }
}

function* changePassword({ payload }) {
  try {
    const { data } = yield call(dxApi.post, `/account/set-password-magic`, payload);

    if (data.success) {
      yield put(Creators.changePasswordSuccess(data.data));
    } else {
      yield put(Creators.changePasswordFailure(data.message));
      yield put(setError(data.message));
    }
  } catch (err) {
    yield put(Creators.changePasswordFailure(err));
    yield put(
      setError('Sorry, could not set your password. Please try again in a minute or two.')
    );
  }
}

export function* loadProfile() {
  try {
    const { data } = yield call(dxApi.get, `/api/profile/profile-nv2`);
    const profileData = data.data;

    yield put(
      Creators.loadProfileSuccess({
        ...profileData,
        vaccineStatus: profileData.vaccineStatus
          ? profileData.vaccineStatus
          : vaccineStatuses.notYetUploaded,
      })
    );
  } catch (err) {
    yield put(Creators.loadProfileFailure(err));
    yield put(setError('Error, could not load your profile'));
  }
}

function* changeFavoriteLocation({ payload }) {
  try {
    const { data } = yield call(dxApi.post, `/contact/change-favorite-location-nv2`, {
      favoriteLocation: payload,
    });

    const { token, user } = data.data;
    authUtils.setToken(token, user.sfId, false);
    api.setAuthTokenToHeader(token);

    yield put(Creators.changeFavoriteLocationSuccess(user));

    // Check if there was an original callback URL saved before redirecting to favorite location selection
    const originalCallback = sessionStorage.getItem('originalCallback');
    if (
      originalCallback &&
      originalCallback !== '/' &&
      originalCallback !== '/onboarding'
    ) {
      yield call(history.push, originalCallback);
      sessionStorage.removeItem('originalCallback');
      sessionStorage.removeItem('callback');
    }
  } catch (err) {
    yield put(Creators.changeFavoriteLocationFailure(err));
    yield put(setError('Hmm, could not change favorite location, try again please'));
  }
}

function* changeProfileInfo({ payload }) {
  try {
    const { data } = yield call(api.put, `/contacts/update_profile`, payload);
    yield put(Creators.changeProfileInfoSuccess(data));
  } catch (err) {
    yield put(Creators.changeProfileInfoFailure(err));
    yield put(
      setError(
        'Sorry, could not update your details, please try again in a minute or two'
      )
    );
  }
}

export function* loadMembership() {
  try {
    const { data } = yield call(api.get, `/contacts/membership`);
    yield put(Creators.loadMembershipSuccess(data.length ? data[0].userGroups : []));
  } catch (err) {
    yield put(Creators.loadMembershipFailure(err));
  }
}

function* checkFamily({ email }) {
  try {
    const { data } = yield call(api.get, `/auth/family?email=${email}`);
    yield put(Creators.checkFamilySuccess(data.isFamily));
  } catch (err) {
    yield put(Creators.checkFamilyFailure(err));
    yield put(
      setError('Sorry, something went wrong, please try again in a minute or two')
    );
  }
}

function* checkMemberStatus() {
  try {
    const { data } = yield call(api.get, `/auth/check-status`);
    yield put(Creators.checkMemberStatusSuccess(data));
  } catch (err) {
    yield put(Creators.checkMemberStatusFailure(err));
    yield put(setError('Sorry, could not fetch status'));
  }
}

function* createMagicLink({ email }) {
  try {
    const { data } = yield call(dxApi.post, `/account/create-magic-link-token`, {
      email,
    });
    if (!data.success) {
      yield put(Creators.createMagicLinkFailure(data.message));
      // yield put(setError(data.message));
    } else {
      yield put(Creators.createMagicLinkSuccess(data.data));
    }
  } catch (err) {
    yield put(Creators.createMagicLinkFailure(err));
    yield put(setError('Failed to create magic link. Please try again.'));
  }
}

function* setVaccineStatus({ payload }) {
  try {
    const { userGroupBody, vaccineStatusBody, isAuth } = payload;

    yield call(api.post, `/membership`, userGroupBody);
    yield call(api.post, `/contacts/vaccine-status`, vaccineStatusBody, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });

    yield put(Creators.setVaccineStatusSuccess());
    yield call(history.push, isAuth ? '/' : '/verify-status?m=ready');
  } catch (err) {
    yield put(Creators.setVaccineStatusFailure(err));
    yield put(setError('Please try again.'));
  }
}

function* validateMagicLink({ token }) {
  try {
    const { data } = yield call(
      dxApi.post,
      `/account/validate-magic-link-token?magicLinkToken=${token}&isPortal=true`
    );
    yield put(Creators.validateMagicLinkSuccess(data));
  } catch (err) {
    yield put(Creators.validateMagicLinkFailure(err));
  }
}

function* setPasswordMagicLink({ payload }) {
  try {
    const { password, hubspotId } = payload;

    const { data } = yield call(dxApi.post, '/account/set-password-magic', {
      password,
      hubspotId,
    });

    if (data.success) {
      yield put(Creators.setPasswordMagicLinkSuccess(data.data));
    } else {
      yield put(Creators.setPasswordMagicLinkFailure(data.message));
      yield put(setError(data.message));
    }
  } catch (err) {
    yield put(Creators.setPasswordMagicLinkFailure(err));
    yield put(setError('Failed to set password. Please try again.'));
  }
}

export function* authRootSaga() {
  yield takeLeading(LOGIN_REQUEST, login);
  yield takeLeading(LOGIN_WITH_SERVICE, loginWithService);
  yield takeLeading(LOGOUT_REQUEST, logout);
  yield takeLatest(LOAD_PROFILE_REQUEST, loadProfile);
  yield takeLatest(LOAD_MEMBERSHIP_REQUEST, loadMembership);
  yield takeLatest(CHANGE_FAVORITE_LOCATION_REQUEST, changeFavoriteLocation);
  yield takeLatest(CHANGE_PROFILE_INFO_REQUEST, changeProfileInfo);
  yield takeLatest(SET_VACCINE_STATUS_REQUEST, setVaccineStatus);
  yield takeLeading(REQUEST_RESET_REQUEST, requestReset);
  yield takeLeading(RESET_PASSWORD_REQUEST, resetPassword);
  yield takeLeading(CHANGE_PASSWORD_REQUEST, changePassword);
  yield takeLeading(UPDATE_PASSWORD_REQUEST, updatePassword);
  yield takeLeading(CREATE_MAGIC_LINK_REQUEST, createMagicLink);
  yield takeLeading(CHECK_MEMBER_STATUS_REQUEST, checkMemberStatus);
  yield takeLeading(VALIDATE_MAGIC_LINK_REQUEST, validateMagicLink);
  yield takeLeading(SET_PASSWORD_MAGIC_LINK_REQUEST, setPasswordMagicLink);

  yield debounce(1000, CHECK_FAMILY_REQUEST, checkFamily);
}
